import { Asterisk } from "./styles";
import { color, fonts } from "@styles";
import { mobile } from "components/templates/Breakpoints";
import styled from "styled-components";
import { SelectHTMLAttributes, useEffect, useRef, useState } from "react";
import DropdownIcon from "../../../public/static/icons/DropdownIcon.svg";
import { useField } from "@unform/core";
import Image from "next/legacy/image";

export interface SelectInputProps extends SelectHTMLAttributes<HTMLSelectElement> {
  name: string;
  placeholder?: string;
  options?: string[];
}

export const SelectInput = ({ placeholder, options, ...rest }: SelectInputProps) => {
  const selectRef = useRef(null);
  const { fieldName, registerField, error, clearError } = useField(rest.name);
  const [currentValue, setCurrentValue] = useState<string | number | readonly string[]>(
    rest.value || "",
  );

  const isPlaceholder = currentValue === "";

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: selectRef,
      getValue: (ref) => {
        return ref.current.value;
      },
      setValue: (ref, value) => {
        ref.current.value = value;
        setCurrentValue(value);
      },
      clearValue: (ref) => {
        ref.current.value = "";
        setCurrentValue("");
      },
    });
  }, [fieldName, registerField]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (error) {
      clearError();
    }

    setCurrentValue(e.currentTarget.value);

    if (rest.onChange) {
      rest.onChange(e);
    }
  };

  return (
    <Relative>
      <ArrowContainer>
        <Image src={DropdownIcon} alt="" />
      </ArrowContainer>
      <Select
        ref={selectRef}
        {...rest}
        $hasError={!!error}
        onChange={handleChange}
        id={rest.name}
        required
        $isPlaceholder={isPlaceholder}
        value={currentValue}
      >
        {[placeholder, ...options].map((selected) => (
          <option key={selected} value={selected}>
            {selected}
          </option>
        ))}
      </Select>
      {isPlaceholder ? (
        <Placeholder>
          {placeholder}
          <Asterisk />
        </Placeholder>
      ) : null}

      {!!error ? <ErrorText>{error}</ErrorText> : null}
    </Relative>
  );
};

const Relative = styled.div<{ $hasError?: boolean }>`
  position: relative;
  align-self: flex-start;
  width: 100%;
`;

const ErrorText = styled.div`
  color: #ff5f5f;
  margin: -0.5rem 0 0.5rem 0;
`;

const Placeholder = styled.div`
  color: ${color.B2100};
  font-family: ${fonts.body};
  font-weight: 400;
  font-size: 1.25rem;
  position: absolute;
  top: 36%;
  left: 1.25rem;
  pointer-events: none;
`;

const Select = styled.select<{ $isPlaceholder?: boolean; $hasError?: boolean }>`
  width: 100%;
  margin: 1rem 0;
  box-sizing: border-box;
  padding: 1rem 3rem 1rem 1.25rem;
  font-weight: 400;
  font-size: 1.25rem;
  color: ${(p) => (p.$isPlaceholder ? "transparent" : color.B1100)};
  border-radius: 0.5rem;
  border: 0.125rem solid ${(p) => (p.$hasError ? "#FF5F5F" : color.B240)};
  appearance: none;
  font-family: ${fonts.body};
  min-height: 3.875rem;
  cursor: pointer;

  option {
    color: ${color.B1100};
  }

  ${mobile} {
    margin: 0.5rem 0;
  }
`;

const ArrowContainer = styled.div`
  position: absolute;
  right: 0;
  padding: 1rem 1.25rem;
  pointer-events: none;
  display: flex;
  align-items: center;
  height: 6rem;

  ${mobile} {
    height: 5rem;
  }
`;
